/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\TimeSettingsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the system module.
----------------------------------------------------------------*/


import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { TimezoneSelect } from '../../../components/ui/TimezoneSelect';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import type { SystemConfig } from '../../../types/system';

interface TimeSettingsBoxProps {
  formData: SystemConfig;
  onInputChange: (field: keyof SystemConfig, value: SystemConfig[keyof SystemConfig]) => void;
}

/**
 * TimeSettingsBox
 *
 * Renders the Time Settings FormBox: timezone selector, date/time inputs,
 * and a live clock display that updates every second.
 */
export function TimeSettingsBox({ formData, onInputChange }: TimeSettingsBoxProps) {
  const { t, i18n } = useI18n();
  const [liveTime, setLiveTime] = useState(new Date());

  // Tick the clock every second
  useEffect(() => {
    const timer = setInterval(() => setLiveTime(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  return (
    <FormBox title="Time Settings" icon={<Clock />}>
      <div className="space-y-3">
        {/* Timezone / Date / Time row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="space-y-1 col-span-2">
            <SimpleTooltip title="Time Zone" content={t('systemMisc.tooltips.timeZone')} position="top" maxWidth="300px">
              <Label htmlFor="timezone" className="text-sm font-medium text-foreground">Time Zone</Label>
            </SimpleTooltip>
            <TimezoneSelect
              value={formData.timezone}
              onChange={(value) => onInputChange('timezone', value)}
            />
          </div>

          <div className="space-y-1">
            <SimpleTooltip title="Date" content={t('systemMisc.tooltips.date')} position="top" maxWidth="300px">
              <Label htmlFor="currentDate" className="text-sm font-medium text-foreground">Date</Label>
            </SimpleTooltip>
            <input
              id="currentDate"
              type="date"
              value={formData.currentDate}
              onChange={(e) => onInputChange('currentDate', e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full"
            />
          </div>

          <div className="space-y-1">
            <SimpleTooltip title="Time" content={t('systemMisc.tooltips.time')} position="top" maxWidth="300px">
              <Label htmlFor="currentTime" className="text-sm font-medium text-foreground">Time</Label>
            </SimpleTooltip>
            <input
              id="currentTime"
              type="time"
              value={formData.currentTime}
              onChange={(e) => onInputChange('currentTime', e.target.value)}
              className="px-3 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full"
            />
          </div>
        </div>

        {/* Live Clock Display */}
        <div className="w-full mt-3 flex justify-center">
          <SimpleTooltip title="Clock Display" content={t('systemMisc.tooltips.clockDisplay')} position="top" maxWidth="300px">
            <div className="w-full p-6 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border-2 border-primary/20 shadow-lg">
              <div className="text-center">
                <div className="text-4xl font-mono font-bold text-primary mb-2 tracking-wider">
                  {liveTime.toLocaleTimeString(locale, { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
                <div className="text-sm text-muted-foreground font-medium">
                  {liveTime.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                <div className="text-xs text-primary/70 mt-1 font-semibold">
                  LIVE CLOCK � {formData.timezone}
                </div>
              </div>
            </div>
          </SimpleTooltip>
        </div>
      </div>
    </FormBox>
  );
}
